import { ArrowRight, ChevronRight } from '@mui/icons-material';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import ListIcon from '@mui/icons-material/List';
import { Box, IconButton, Paper, Tab, Tabs, Typography } from '@mui/material';
import { emptyCells } from 'common/constants';
import { ICategory, IExpense } from 'common/entities';
import { Currency, IMonth } from 'common/types';
import CategoryIcon from 'components/CategoryIcons/CategoryIcon';
import palette from 'components/CategoryIcons/pallete';
import PieChart from 'components/charts/PieChart';
import { LabeledCheckbox } from 'components/controls/formControls';
import ToggleButtonGroup from 'components/Shared/ToggleButtonGroup';
import { useAppSelector } from 'core/hooks/hooks';
import { useCurrency } from 'core/hooks/useCurrency';
import { sumWithCurrency } from 'core/services/currency.service';
import { getCategorySpent } from 'core/services/expense.service';
import { budgetsSelector } from 'core/store/selectors/budgets.selector';
import { categoriesSelector } from 'core/store/selectors/categories.selector';
import { expensesSelector } from 'core/store/selectors/expenses.selector';
import { periodSelector } from 'core/store/selectors/period.selector';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import MonthBudgetExpensesList from './MonthBudgetExpensesList';

interface MonthCategoryExpensesListViewProps {
  expenses: IExpense[];
  categories: ICategory[];
}

interface MonthCategoryExpensesPieViewProps
  extends MonthCategoryExpensesListViewProps {}

interface CategoryEntry {
  category?: ICategory;
  spent: number;
  children?: CategoryEntry[];
}

interface MonthCategoryExpensesListViewItemProps extends CategoryEntry {
  max: number;
  child?: boolean;
  expanded?: boolean;
  onExpand?: (id: string) => void;
}

interface SpentProgressProps {
  spent: number;
  max: number;
  width?: number;
}

const SpentProgress = ({ spent, max, width }: SpentProgressProps) => {
  return (
    <Box
      sx={{
        flexGrow: 1,
        width: width ? `${width}px` : '150px',
        height: 16,
        borderRadius: 3,
        backgroundColor: '#FBFBF5',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: theme => `inset 0 0 1px ${theme.palette.grey[500]}`
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: `${(spent / max) * 100}%`,
          height: '100%',
          backgroundColor: 'primary.main'
        }}
      ></Box>
    </Box>
  );
};

const MonthCategoryExpensesListViewItem = ({
  category,
  spent,
  max,
  children,
  child = false,
  expanded,
  onExpand
}: MonthCategoryExpensesListViewItemProps) => {
  const { currency } = useCurrency();
  const hasChildren = !!children?.length;
  return (
    <Box sx={{ mb: 1 }}>
      <Box
        sx={{
          px: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: expanded ? '#f9f9f9' : undefined,
          borderLeft: expanded ? '1px solid #ccc' : undefined,
          pt: expanded ? 1 : 0,
          '&:hover': {
            backgroundColor: hasChildren ? '#f9f9f9' : undefined,
            cursor: hasChildren ? 'pointer' : 'default'
          }
        }}
        onClick={() =>
          category && hasChildren && onExpand && onExpand(category?.id)
        }
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center'
          }}
        >
          <Box sx={{ mr: 1 }}>
            <CategoryIcon
              size="small"
              color={category?.color}
              icon={category?.icon}
            />
          </Box>
          <Box sx={{ width: '140px', display: 'flex', alignItems: 'center' }}>
            {category ? (
              <>
                <Link
                  to={{
                    pathname: `/category/${category.id}`,
                    state: { goBackTitle: 'Back to expenses' }
                  }}
                  style={{ color: 'initial' }}
                >
                  {category.name}
                </Link>
              </>
            ) : (
              <>{emptyCells.category}</>
            )}
            {hasChildren && (
              <IconButton
                size="small"
                sx={{ transform: 'scale(0.8)' }}
                onClick={() =>
                  category?.id && onExpand && onExpand(category?.id)
                }
              >
                <ChevronRight
                  sx={{ transform: expanded ? 'rotate(90deg)' : undefined }}
                />
              </IconButton>
            )}
          </Box>
        </Box>
        <Box
          sx={{
            mr: 3,
            display: {
              xs: 'none',
              sm: 'block'
            }
          }}
        >
          <SpentProgress
            max={max}
            spent={spent}
            width={child ? 134 : undefined}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            width: '80px'
          }}
        >
          <Box>{sumWithCurrency(spent, currency)}</Box>
        </Box>
      </Box>
      {hasChildren && expanded && (
        <Box
          sx={{
            py: 1,
            pl: 2,
            pb: '1px',
            mb: 2,
            borderRadius: '0 0 6px 0',
            backgroundColor: '#f9f9f9',
            borderLeft: '1px solid #ccc '
          }}
        >
          {children?.map(child => (
            <MonthCategoryExpensesListViewItem
              key={child.category?.id}
              {...child}
              child
              max={spent}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

const createCategoryEntry = (
  category: ICategory | undefined,
  expenses: IExpense[],
  categories: ICategory[],
  month: IMonth,
  currency: Currency,
  spentWithChildren: boolean = true
): CategoryEntry => {
  const children =
    category && !category.parentCategoryId && spentWithChildren
      ? categories
          .filter(cat => cat.parentCategoryId === category.id)
          .map(cat =>
            createCategoryEntry(
              cat,
              expenses,
              categories,
              month,
              currency,
              false
            )
          )
      : [];
  return {
    category,
    spent: getCategorySpent(
      category?.id,
      expenses,
      categories,
      month,
      currency,
      spentWithChildren
    ),
    children: children.length
      ? [
          createCategoryEntry(
            category,
            expenses,
            categories,
            month,
            currency,
            false
          ),
          ...children
        ]
      : undefined
  };
};

const createPieEntries = (
  selectedCategories: ICategory[],
  allCategories: ICategory[],
  expenses: IExpense[],
  month: IMonth,
  currency: Currency,
  parentCategory?: ICategory
) => {
  const { total, entries } = selectedCategories.reduce(
    ({ total, entries }, current) => {
      const spent = getCategorySpent(
        current.id,
        expenses,
        allCategories,
        month,
        currency,
        true
      );
      entries.push({
        category: current,
        spent
      });
      total += spent;
      return { total, entries };
    },
    {
      total: 0,
      entries: parentCategory
        ? [
            {
              category: parentCategory,
              spent: getCategorySpent(
                parentCategory.id,
                expenses,
                allCategories,
                month,
                currency,
                false
              )
            }
          ]
        : []
    } as {
      total: number;
      entries: CategoryEntry[];
    }
  );

  return { total, entries };
};

const MonthCategoryExpensesListView = ({
  expenses,
  categories
}: MonthCategoryExpensesListViewProps) => {
  const { currency } = useCurrency();
  const [showZero, setShowZero] = useState(false);
  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const month = useAppSelector(periodSelector);
  const categoryEntries = useMemo(
    () =>
      categories
        .filter(cat => !cat.parentCategoryId)
        .map(cat =>
          createCategoryEntry(cat, expenses, categories, month, currency)
        )
        .filter(item => showZero || !!item.spent)
        .sort((a, b) => b.spent - a.spent),
    [categories, expenses, month, showZero, currency]
  );

  const maxSpent = useMemo(
    () => categoryEntries.reduce((max, entry) => Math.max(max, entry.spent), 0),
    [categoryEntries]
  );

  const handleExpandCategory = (categoryId: string) => {
    if (expandedIds.includes(categoryId)) {
      setExpandedIds(expandedIds.filter(id => id !== categoryId));
    } else {
      setExpandedIds([...expandedIds, categoryId]);
    }
  };

  const isCategoryExpanded = (categoryId: string) =>
    expandedIds.includes(categoryId);

  return (
    <Box sx={{ pt: 1 }}>
      <Box sx={{ mb: 2 }}>
        <LabeledCheckbox
          id="showZeroSpent"
          name="showZeroSpent"
          label="Show zero spent"
          handleChange={() => setShowZero(!showZero)}
          isChecked={showZero}
        />
      </Box>
      <Box sx={{ mx: -1 }}>
        {categoryEntries.map(entry => (
          <MonthCategoryExpensesListViewItem
            key={entry.category?.id}
            max={maxSpent}
            onExpand={entry.children?.length ? handleExpandCategory : undefined}
            expanded={
              !!entry.category?.id && isCategoryExpanded(entry.category.id)
            }
            {...entry}
          />
        ))}
      </Box>
    </Box>
  );
};

const MonthCategoryExpensesPieView = ({
  categories,
  expenses
}: MonthCategoryExpensesPieViewProps) => {
  const { currency } = useCurrency();
  const [currentChildId, setCurrentChildId] = useState<string | null>(null);
  const month = useAppSelector(periodSelector);
  const currentChild = useMemo(
    () => categories.find(cat => cat.id === currentChildId),
    [currentChildId, categories]
  );
  const targetCategories = useMemo(
    () =>
      categories.filter(
        cat =>
          (!currentChildId && !cat.parentCategoryId) ||
          cat.parentCategoryId === currentChildId
      ),
    [currentChildId, categories]
  );

  const { total, entries: categoryEntries } = useMemo(() => {
    const { total, entries } = createPieEntries(
      targetCategories,
      categories,
      expenses,
      month,
      currency,
      currentChild
    );
    return {
      total,
      entries: entries
        .filter(item => !!item.spent)
        .sort((a, b) => b.spent - a.spent)
    };
  }, [targetCategories, categories, expenses, month, currentChild]);

  const handleItemClick = (index: number) => {
    const entry = categoryEntries[index];
    if (
      entry &&
      entry.category &&
      categories.some(cat => cat.parentCategoryId === entry.category?.id)
    ) {
      setCurrentChildId(entry.category.id);
    }
  };

  return (
    <Box>
      <Box
        sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', pt: 2 }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            '& > *': { color: '#555' }
          }}
        >
          <Typography
            component={'span'}
            sx={
              currentChild
                ? { cursor: 'pointer', textDecoration: 'underline' }
                : {}
            }
            onClick={() => setCurrentChildId(null)}
          >
            Categories
          </Typography>
          {currentChild && <ArrowRight />}
          {currentChild && (
            <Typography component={'span'}>{currentChild.name}</Typography>
          )}
        </Box>

        <Typography variant="subtitle2">
          Total: {sumWithCurrency(total, currency)}
        </Typography>
      </Box>
      <PieChart
        onItemClick={handleItemClick}
        data={{
          labels: categoryEntries.map(
            entry => entry.category?.name ?? emptyCells.category
          ),
          datasets: [
            {
              data: categoryEntries.map(entry => entry.spent),
              backgroundColor: categoryEntries.map(entry =>
                entry.category?.color
                  ? palette[entry.category.color]?.color
                  : '#346439'
              ),
              hoverOffset: 30,
              datalabels: {
                color(context) {
                  const targetColor =
                    categoryEntries[context.dataIndex].category?.color;
                  const defaultColor = '#fff';

                  return targetColor
                    ? palette[targetColor].fontColor ?? defaultColor
                    : defaultColor;
                },
                formatter(value, context) {
                  const percentage = (value / total) * 100;

                  return (categoryEntries.length > 10 && percentage > 3) ||
                    percentage > 10
                    ? `${categoryEntries[context.dataIndex].category?.name}`
                    : '';
                }
              }
            }
          ]
        }}
        tooltipFormatter={function (tooltipItem) {
          const dataIndex = tooltipItem.dataIndex;
          const dataset = tooltipItem.dataset;
          return sumWithCurrency(dataset.data?.[dataIndex], currency);
        }}
      />
    </Box>
  );
};

const MonthExpenses = () => {
  const { expenses } = useAppSelector(expensesSelector());
  const { categories } = useAppSelector(categoriesSelector);
  const { budgets } = useAppSelector(budgetsSelector);
  const [viewTab, setViewTab] = useState('list');
  const [currentMode, setCurrentMode] = useState<'categories' | 'budgets'>(
    'categories'
  );

  const handleSwitchMode = (tabIndex: number) => {
    setCurrentMode(tabIndex === 0 ? 'categories' : 'budgets');
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5">Month Expenses</Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mb: 2,
          alignItems: 'center'
        }}
      >
        <Box>
          <Tabs
            value={currentMode === 'categories' ? 0 : 1}
            onChange={(_, tabIndex: number) => handleSwitchMode(tabIndex)}
            sx={{ flexGrow: 1 }}
          >
            <Tab key="categories" label="Categories" />
            <Tab key="budgets" label="Budgets" />
          </Tabs>
        </Box>
        <ToggleButtonGroup
          buttons={[
            { id: 'list', label: 'List', icon: <ListIcon /> },
            { id: 'pie', label: 'Pie', icon: <DonutLargeIcon /> }
          ]}
          value={viewTab}
          onChange={setViewTab}
        />
      </Box>
      {currentMode === 'categories' && viewTab === 'list' && (
        <MonthCategoryExpensesListView
          expenses={expenses}
          categories={categories}
        />
      )}
      {currentMode === 'categories' && viewTab === 'pie' && (
        <MonthCategoryExpensesPieView
          expenses={expenses}
          categories={categories}
        />
      )}
      {currentMode === 'budgets' && viewTab === 'list' && (
        <MonthBudgetExpensesList budgets={budgets} expenses={expenses} />
      )}
    </Paper>
  );
};

export default MonthExpenses;
